<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { formatNumber } from '@/app/core/util/numbers'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import dayjs from 'dayjs'
import {
  DSelectFieldSearch,
  DTextField,
  DBtn,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon,
  AnnotationInfoIcon
} from 'front-library'
import {
  generateIncidents,
  getReportRequest
} from '@/app/core/services/reports'
import {
  getIncidents,
  Incident,
  deleteIncident,
  getListEmployees,
  getListConcepts,
  getPayroll,
  getDatesCalendar
} from '@/app/core/services/incidents'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const storePayroll = usePayrollStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos */
const tableIncident = ref(null)
const IncidentForDelete = ref()
const itemsEmployees = ref([] as any[])
const searchEmployees = ref('')
const employeeId = ref()
const itemsConcepts = ref([] as any[])
const searchConcepts = ref('')
const conceptId = ref()
const searchDateIni = ref()
const searchDateEnd = ref()
const lastPayment = ref()
const urlFile = ref(null)
const refresh = ref()

/* Datos componente table */
const columns = ref([
  { name: 'employee', title: t('incidents.list.employee') },
  { name: 'concept', title: t('incidents.list.concept') },
  { name: 'typeProcessCode', title: t('incidents.list.process') },
  { name: 'startDate_i', title: t('incidents.list.startDate') },
  { name: 'endDate_i', title: t('incidents.list.endDate') },
  {
    name: 'amount',
    title: t('incidents.list.amount'),
    format: (value: any) => formatNumber(value),
    align: 'end'
  },
  {
    name: 'quantity',
    title: t('incidents.list.quantity'),
    format: (value: any) => formatNumber(value),
    align: 'end'
  },
  { name: 'percentage_i', title: t('incidents.list.percentage') },
  { name: 'actions', title: '' }
])
const rows = ref<Incident[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createIncident = () => {
  void router.push({ name: 'CreateIncident' })
}
const updateIncident = (id: any) => {
  void router.push({ name: 'UpdateIncident', params: { id } })
}
const getIncident = (id: any) => {
  void router.push({ name: 'GetIncident', params: { id } })
}

/* Eliminar incidencia */
const removeIncident = (values: any) => {
  typeAlertModal.value = 'question'
  titleModal.value = t('incidents.list.actionStatus', {
    status: t('table.action.delete')
  })
  descriptionModal.value = t('modal.description')
  showModal.value = true
  IncidentForDelete.value = values.id
}
const deleteConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await deleteIncident(IncidentForDelete.value)
    .then(response => {
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('incidents.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void filterIncidences()
    })
    .catch(async (error: any) => {
      notificationError(error, 'incidents', t, notifications, loading)
    })
}

const setDates = async () => {
  const dates = await getDatesCalendar(
    storePayroll.payroll?.id ?? '',
    lastPayment.value.last_payment
  )
  searchDateIni.value = dayjs(dates.start_date)
    .add(1, 'days')
    .format('YYYY-MM-DD')
  searchDateEnd.value = dayjs(dates.end_date)
    .add(1, 'days')
    .format('YYYY-MM-DD')
}

/* Listar incidencias filtradas */
const filterIncidences = async () => {
  loading.value = true
  if (searchDateIni.value === undefined || searchDateEnd.value === undefined) {
    loading.value = false
  } else if (searchDateIni.value === '' || searchDateEnd.value === '') {
    notifications.addNotification({
      title: t('incidents.list.startDateGreaterEndDate'),
      type: 'error',
      time: 10000
    })
    loading.value = false
  } else if (
    Date.parse(searchDateIni.value) > Date.parse(searchDateEnd.value)
  ) {
    notifications.addNotification({
      title: t('incidents.list.endDateLessStartDate'),
      type: 'error',
      time: 10000
    })
    loading.value = false
  } else {
    const p1 = new Promise(resolve => {
      resolve(
        getIncidents(
          offset.value,
          perPage.value,
          searchDateIni.value,
          searchDateEnd.value,
          employeeId.value,
          conceptId.value
        )
      )
    })
    const p2 = new Promise(resolve => {
      resolve(
        getIncidents(
          0,
          10000,
          searchDateIni.value,
          searchDateEnd.value,
          employeeId.value,
          conceptId.value
        )
      )
    })
    Promise.all([p1, p2])
      .then(async (results: any) => {
        // Mostrar datos en la tabla
        rows.value = results[0]
        totalRows.value = results[1].length
        loading.value = false
      })
      .catch(async function (error: any) {
        notificationError(error, 'incidents', t, notifications, loading)
      })
  }
}

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const payroll = storePayroll.payroll?.id ?? ''
    const concepts =
      conceptId.value === undefined
        ? itemsConcepts.value
            .filter(item => item.id !== null && item.id !== '')
            .map(item => item.id)
        : [conceptId.value]
    const employees =
      employeeId.value === undefined
        ? itemsEmployees.value
            .filter(item => item.id !== null && item.id !== '')
            .map(item => item.id)
        : [employeeId.value]
    const data = {
      code: 'basics/incidents',
      parameters: {
        payrolls: [payroll],
        processes: ['E', 'T', 'N', 'V', 'U', 'L', 'A'],
        concepts,
        startDate: searchDateIni.value,
        endDate: searchDateEnd.value,
        employees
      }
    }
    const id = await generateIncidents(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'incidents', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  urlFile.value = null
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    // fechas iniciales y finales en buscador
    lastPayment.value = await getPayroll(storePayroll.payroll?.id ?? '')
    if (lastPayment.value.last_payment == null) {
      searchDateIni.value = dayjs().format('YYYY-MM-DD')
      searchDateEnd.value = dayjs().add(1, 'days').format('YYYY-MM-DD')
    } else {
      await setDates()
    }
    const p1 = new Promise(resolve => {
      resolve(
        getIncidents(
          offset.value,
          perPage.value,
          searchDateIni.value,
          searchDateEnd.value,
          employeeId.value,
          conceptId.value
        )
      )
    })
    const p2 = new Promise(resolve => {
      resolve(
        getIncidents(
          0,
          10000,
          searchDateIni.value,
          searchDateEnd.value,
          employeeId.value,
          conceptId.value
        )
      )
    })
    const p3 = new Promise(resolve => {
      resolve(getListEmployees(storePayroll.payroll?.id ?? ''))
    })
    const p4 = new Promise(resolve => {
      resolve(getListConcepts(storePayroll.payroll?.id ?? ''))
    })
    Promise.all([p1, p2, p3, p4])
      .then(async (results: any) => {
        loading.value = false
        itemsEmployees.value = results[2]
        itemsEmployees.value.unshift({
          id: '',
          name: t('incidents.list.allEmployees')
        })
        itemsConcepts.value = results[3]
        itemsConcepts.value.unshift({
          id: '',
          name: t('incidents.list.allConcepts')
        })
        // Mostrar datos en la tabla
        rows.value = results[0]
        totalRows.value = results[1].length
        loading.value = false
      })
      .catch(async function (error: any) {
        notificationError(error, 'incidents', t, notifications, loading)
      })
  } catch (error: any) {
    notificationError(error, 'incidents', t, notifications, loading)
  }
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="
      t('modal.acceptQuestion', {
        action: t('table.action.delete')
      })
    "
    @closeModal="closeModal"
    @otherMethod="deleteConfirm">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-3">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('incidents.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('incidents.list.description')" />
      </div>
      <div class="lg:flex justify-between pt-5 gap-4">
        <div class="grid grid-cols-10 md:grid-cols-11 items-end gap-3">
          <DSelectFieldSearch
            class="col-span-10 sm:col-span-5 md:col-span-2"
            :label="t('incidents.list.employee')"
            v-model="employeeId"
            :options="filterOptions(itemsEmployees, searchEmployees)"
            @filter="searchEmployees = $event" />
          <DSelectFieldSearch
            class="col-span-10 sm:col-span-5 md:col-span-2"
            :label="t('incidents.list.concept')"
            v-model="conceptId"
            :options="filterOptions(itemsConcepts, searchConcepts)"
            @filter="searchConcepts = $event" />
          <DTextField
            class="col-span-10 sm:col-span-5 md:col-span-2"
            type="date"
            :label="t('fields.startDate')"
            v-model="searchDateIni" />
          <DTextField
            class="col-span-10 sm:col-span-5 md:col-span-2"
            type="date"
            :label="t('fields.endDate')"
            v-model="searchDateEnd" />
          <DBtn
            class="h-10 col-span-10 md:col-span-3 lg:col-span-2"
            @click.prevent="filterIncidences()"
            v-html="t('form.button.search')" />
        </div>
        <div class="flex items-end gap-2 pt-3">
          <DBtn
            class="h-10 w-44"
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
          <DBtn
            class="h-10 w-44"
            :icon="'PlusIcon'"
            type="button"
            @click="createIncident()"
            v-if="store.hasPermission('incident:create')">
            {{
              t('form.button.create', {
                form: t('incidents.list.title', 0)
              })
            }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableIncident"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :pagination-limit="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), filterIncidences()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), filterIncidences()">
      <template #employee="data: any">
        {{ data.employeeCode + ' - ' + data.employeeName }}
      </template>
      <template #concept="data: any">
        {{ data.conceptCode + ' - ' + data.conceptName }}
      </template>
      <template #startDate_i="data: any">
        {{ dayjs(data.startDate).format('DD/MM/YYYY') }}
      </template>
      <template #endDate_i="data: any">
        {{ dayjs(data.endDate).format('DD/MM/YYYY') }}
      </template>
      <template #percentage_i="data: any">
        <div v-if="data.percentage">
          {{
            Number(data.percentage / 100).toLocaleString('en-us', {
              style: 'percent',
              minimumFractionDigits: 0,
              maximumFractionDigits: 5
            })
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="
              (data.reference && data.reference.includes('Vacation')) ||
              (data.reference && data.reference.includes('License')) ||
              data.closeId !== null
            "
            arrow
            offsetDistance="12"
            :content="
              data.reference && data.reference.includes('Vacation')
                ? t('incidents.list.refVacation')
                : data.reference && data.reference.includes('License')
                ? t('incidents.list.refLicense')
                : t('incidents.list.closePayroll')
            "
            :hover="true"
            placement="left"
            class="tooltip info">
            <div class="bg-blue-50 rounded-md py-2">
              <AnnotationInfoIcon
                class="h-6 w-6 mx-2 cursor-pointer text-blue-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('incident:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getIncident(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              data.closeId === null &&
              data.reference === null &&
              store.hasPermission('incident:update')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateIncident(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              data.closeId === null &&
              data.reference === null &&
              store.hasPermission('incident:delete')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeIncident(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
<style>
.info {
  --popper-theme-background-color: #545ffc !important;
  --popper-theme-background-color-hover: #545ffc !important;
}
.info .popper {
  width: 137px !important;
}
</style>
